import { useEffect, useState, type JSX } from 'react';

import { Moon, Sun } from 'lucide-react';

import styles from './ThemeToggle.module.css';

function readMode(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-color-mode') === 'dark' ? 'dark' : 'light';
}

/**
 * Light/dark theme toggle. Owns the `data-color-mode` attribute on <html>,
 * persists the choice to localStorage ('theme'), and follows the OS preference
 * until the user makes an explicit choice. The boot-time default is set in
 * index.html; this keeps it in sync once React mounts.
 */
export function ThemeToggle(): JSX.Element {
  const [mode, setMode] = useState<'light' | 'dark'>(readMode);

  // Follow the OS preference while the user hasn't pinned a choice.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent): void => {
      if (localStorage.getItem('theme')) return;
      const next = e.matches ? 'dark' : 'light';
      document.documentElement.setAttribute('data-color-mode', next);
      setMode(next);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const toggle = (): void => {
    const next = mode === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-color-mode', next);
    localStorage.setItem('theme', next);
    setMode(next);
  };

  const isDark = mode === 'dark';
  return (
    <button
      type="button"
      className={styles.Toggle}
      onClick={toggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
    >
      {isDark ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
    </button>
  );
}

export default ThemeToggle;
